import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AntApp from 'antd/lib/app';
import { CookiesProvider } from 'react-cookie';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppContextProvider } from './AppContextProvider';
import { GreenButtonConnect } from './components/greenButtonConnect/greenButtonConnect';
import { PrivateRoute, ProvideAuth } from './domain/auth/useAuth';
import { AuthLayout } from './layouts/auth/AuthLayout';
import { PrivateLayout } from './layouts/private/PrivateLayout';
import { PublicLayout } from './layouts/public/PublicLayout';
import { AgreementStatusesPage } from './pages/adminDashboard/agreementStatuses/AgreementStatusesPage';
import { ReadingsAvailabilityStats } from './pages/adminDashboard/ReadingsAvailabilityStats';
import { ReadingsAvailabilityStatsByCompany } from './pages/adminDashboard/ReadingsAvailabilityStatsByCompany';
import { ReadingsAvailabilityStatsByEnrollment } from './pages/adminDashboard/ReadingsAvailabilityStatsByEnrollment';
import { RecentlyCreatedOrClosedSitesReporting } from './pages/adminDashboard/RecentlyCreatedOrClosedSitesReporting';
import { EmailUtilityAutomations } from './pages/adminDashboard/EmailUtilityAutomations';
import { Caiso } from './pages/caiso/caiso';
import { CmriView, DrrsView, SibrView } from './pages/caiso/view';
import { CleanResponse } from './pages/cleanResponse/CleanResponse';
import { Companies } from './pages/companies/Companies';
import { CompleteSignup } from './pages/completeSignup/completeSignup';
import { Dashboard } from './pages/dashboard/Dashboard';
import { EnergyUsageSidebarFiltersProvider } from './pages/energyUsageData/sidebar/context/EnergyUsageSidebarFiltersContext';
import { SidebarFilterOptionsProvider } from './pages/energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { EnergyUsageDataAnalytics } from './pages/energyUsageData/view/EnergyUsageDataAnalytics';
import { EnergyUsageDataConnect } from './pages/energyUsageData/view/EnergyUsageDataConnect';
import { EnergyUsageDataSummary } from './pages/energyUsageData/view/EnergyUsageDataSummary';
import { Events } from './pages/events/Events';
import { EventTrackingSidebarFiltersProvider } from './pages/events/sidebar/EventTrackingSidebarFiltersContext';
import { EventCalendarView } from './pages/events/view/EventCalendarView';
import { EventHistoryView } from './pages/events/view/EventHistoryView';
import { EventTrackingView } from './pages/events/view/EventTrackingView';
import { Impact } from './pages/impact/impact';
import { Login } from './pages/login/login';
import { NyisoResourcesList } from './pages/nyiso/NyisoResourcesList';
import { PjmLocations } from './pages/pjm/PjmLocations/PjmLocations';
import { PjmRegistrations } from './pages/pjm/PjmRegistrations/PjmRegistrations';
import { PriceResponse } from './pages/priceResponse/view/triggers/PriceResponse';
import { PriceResponseReporting } from './pages/priceResponseReporting/PriceResponseReporting';
import { CustomerUtilityBill } from './pages/customerUtilityBill/CustomerUtilityBill';
import { CustomerUtilityBillFiles } from './pages/customerUtilityBill/CustomerUtilityBillFiles';
import { BillFilesSidebarFiltersProvider } from './pages/customerUtilityBill/billFilesSidebar/BillFilesSidebarFiltersContext';
import { Programs } from './pages/programs/Programs';
import { ResetPassword } from './pages/resetPassword/ResetPassword';
import { ResetPasswordComplete } from './pages/resetPasswordComplete/resetPasswordComplete';
import { Settlements } from './pages/settlements/Settlements';
import { ReadinessTrackingSidebarFiltersProvider } from './pages/sites/readinessTrackingSidebar/ReadinessTrackingSidebarFiltersContext';
import { NominationReadinessTracking } from './pages/sites/view/NominationReadinessTracking';
import { SitesSidebarFiltersProvider } from './pages/sites/sidebar/SitesSidebarFiltersContext';
import { Sites } from './pages/sites/Sites';
import { Users } from './pages/users/Users';
import { CloudVenSidebarFiltersProvider } from './pages/ven/cloudVenSidebar/CloudVenSidebarFiltersContext';
import { CustomerVenSidebarFiltersProvider } from './pages/ven/customerVenSidebar/CustomerVenSidebarFiltersContext';
import { Vens as VenDevices } from './pages/ven/vens';
import { CloudVenView, CustomerVenView, OverviewView } from './pages/ven/view';
import { queryClient } from './query-client';
import { PeakLoadForecast } from './pages/peak-load-forecast/PeakLoadForecast';
import { PeakLoadForecastProvider } from './pages/peak-load-forecast/PeakLoadForecastContext';
import { BrandingProvider } from './domain/company/branding/BrandingProvider';
import { SalesforceProducts } from './pages/adminDashboard/salesforceProducts/SalesforceProducts';
import { RecentlyDeletedObjects } from './pages/adminDashboard/RecentlyDeletedObjects';
import { EventPerformanceByBatchId } from './pages/eventAnalytics/EventPerformanceByBatchId';
import { EventPerformanceBySite } from './pages/eventAnalytics/EventPerformanceBySite';
import { Nominations } from './pages/sites/view/Nominations';
import { NominationSidebarFiltersProvider } from './pages/sites/nominationSidebar/NominationSidebarFiltersContext';
import { EventPerformanceSidebarFiltersProvider } from './pages/eventAnalytics/sidebar/EventPerformanceSidebarFiltersContext';
import { AgreementStatusesSidebarFiltersProvider } from './pages/adminDashboard/agreementStatuses/sidebar/AgreementStatusesSidebarFiltersContext';
import { CmriOverviewSidebarFiltersProvider } from './pages/caiso/view/CmriView/view/sidebar/CmriOverviewSidebarFiltersContext';
import { VendorBillItems } from './pages/settlements/view/vendorBillItems/VendorBillItems';
import { VendorBillItemSidebarFiltersProvider } from './pages/settlements/view/vendorBillItems/sidebar/VendorBillItemSidebarFiltersContext';
import { PjmLocationSidebarFiltersProvider } from './pages/pjm/PjmLocations/components/PjmLocationSidebar/PjmLocationSidebarFiltersContext';
import { PjmRegistrationSidebarFiltersProvider } from './pages/pjm/PjmRegistrations/components/PjmRegistrationSidebar/PjmRegistrationSidebarFiltersContext';

import './App.css';
import { EventCompanyParticipationView } from './pages/events/view/EventCompanyParticipationView';
import { EventCompanyParticipationSidebarFiltersProvider } from './pages/events/EventCompanyParticipationSidebar/EventCompanyParticipationSidebarFiltersContext';
import { PjmPollingStatus } from './pages/pjm/PjmPollingStatus/PjmPollingStatus';
import { PriceResponseScenarios } from './pages/priceResponse/view/scenarios/PriceResponseScenarios';

export function App() {
    return (
        <AntApp>
            <div className="App">
                <QueryClientProvider client={queryClient}>
                    <CookiesProvider>
                        <ProvideAuth>
                            <BrandingProvider>
                                <AppContextProvider>
                                    <Routes>
                                        <Route path="/:url*(/+)" element={<Navigate to={document.location.pathname.slice(0, -1)} />} />
                                        <Route element={<AuthLayout />}>
                                            <Route path="/login" element={<Login />} />
                                            <Route path="/reset-password" element={<ResetPassword />} />
                                            <Route path="/reset-password/complete/token/:token" element={<ResetPasswordComplete />} />
                                            <Route path="/complete-signup/token/:token" element={<CompleteSignup />} />
                                        </Route>
                                        <Route element={<PublicLayout />}>
                                            <Route path="/greenbutton" element={<GreenButtonConnect />} />
                                        </Route>
                                        <Route element={<PrivateRoute />}>
                                            <Route element={<PrivateLayout />}>
                                                <Route path="/companies" element={<Companies />} />
                                                <Route
                                                    path="/sites"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <SitesSidebarFiltersProvider>
                                                                <Sites />
                                                            </SitesSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route
                                                    path="/nominations"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <NominationSidebarFiltersProvider>
                                                                <Nominations />
                                                            </NominationSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route
                                                    path="/nomination-readiness-tracking"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <ReadinessTrackingSidebarFiltersProvider>
                                                                <NominationReadinessTracking />
                                                            </ReadinessTrackingSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route path="/ven-devices" element={<VenDevices />}>
                                                    <Route index element={<Navigate to="/" replace />} />
                                                    <Route path="overview" element={<OverviewView />} />
                                                    <Route
                                                        path="cloud-ven"
                                                        element={
                                                            <SidebarFilterOptionsProvider>
                                                                <CloudVenSidebarFiltersProvider>
                                                                    <CloudVenView />
                                                                </CloudVenSidebarFiltersProvider>
                                                            </SidebarFilterOptionsProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="customer-ven"
                                                        element={
                                                            <SidebarFilterOptionsProvider>
                                                                <CustomerVenSidebarFiltersProvider>
                                                                    <CustomerVenView />
                                                                </CustomerVenSidebarFiltersProvider>
                                                            </SidebarFilterOptionsProvider>
                                                        }
                                                    />
                                                </Route>
                                                <Route path="/energy-usage-data/summary" element={<EnergyUsageDataSummary />} />
                                                <Route path="/energy-usage-data/connect" element={<EnergyUsageDataConnect />} />
                                                <Route
                                                    path="/energy-usage-data/analytics"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <EnergyUsageSidebarFiltersProvider>
                                                                <EnergyUsageDataAnalytics />
                                                            </EnergyUsageSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route path="/reporting/price-response" element={<PriceResponseReporting />} />
                                                <Route path="/reporting/customer-utility-bills" element={<CustomerUtilityBill />} />
                                                <Route
                                                    path="/reporting/customer-utility-bills/files"
                                                    element={
                                                        <BillFilesSidebarFiltersProvider>
                                                            <CustomerUtilityBillFiles />
                                                        </BillFilesSidebarFiltersProvider>
                                                    }
                                                />
                                                {/*<Route path="/reporting/power-bi" element={<PowerBIEmbeddedReport />} />*/}
                                                <Route path="/settlement-files" element={<Settlements />} />
                                                <Route
                                                    path="/settlement-data"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <VendorBillItemSidebarFiltersProvider>
                                                                <VendorBillItems />
                                                            </VendorBillItemSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />

                                                <Route path="events" element={<Events />}>
                                                    <Route index path="calendar" element={<EventCalendarView />} />
                                                    <Route path="history" element={<EventHistoryView />} />
                                                    <Route
                                                        path="tracking"
                                                        element={
                                                            <SidebarFilterOptionsProvider>
                                                                <EventTrackingSidebarFiltersProvider>
                                                                    <EventTrackingView />
                                                                </EventTrackingSidebarFiltersProvider>
                                                            </SidebarFilterOptionsProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="company-participation"
                                                        element={
                                                            <SidebarFilterOptionsProvider>
                                                                <EventCompanyParticipationSidebarFiltersProvider>
                                                                    <EventCompanyParticipationView />
                                                                </EventCompanyParticipationSidebarFiltersProvider>
                                                            </SidebarFilterOptionsProvider>
                                                        }
                                                    />
                                                </Route>

                                                <Route path="/programs" element={<Programs />} />
                                                <Route path="/price-response/triggers" element={<PriceResponse />} />
                                                <Route path="/price-response/scenarios" element={<PriceResponseScenarios />} />
                                                <Route path="/clean-response/triggers" element={<CleanResponse />} />
                                                <Route
                                                    path="/event-analytics/performance"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <EventPerformanceSidebarFiltersProvider>
                                                                <EventPerformanceByBatchId />
                                                            </EventPerformanceSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route path="/event-analytics/performance/:batchId" element={<EventPerformanceBySite />} />
                                                <Route path="/users" element={<Users />} />
                                                <Route path="/impact" element={<Impact />} />
                                                <Route path="caiso" element={<Caiso />}>
                                                    <Route path="sibr/*" element={<SibrView />} />
                                                    <Route path="drrs/*" element={<DrrsView />} />
                                                    <Route
                                                        path="cmri/*"
                                                        element={
                                                            <SidebarFilterOptionsProvider>
                                                                <CmriOverviewSidebarFiltersProvider>
                                                                    <CmriView />
                                                                </CmriOverviewSidebarFiltersProvider>
                                                            </SidebarFilterOptionsProvider>
                                                        }
                                                    />
                                                </Route>

                                                <Route
                                                    path="/pjm/locations"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <PjmLocationSidebarFiltersProvider>
                                                                <PjmLocations />
                                                            </PjmLocationSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route
                                                    path="/pjm/registrations"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <PjmRegistrationSidebarFiltersProvider>
                                                                <PjmRegistrations />
                                                            </PjmRegistrationSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route path="/pjm/polling-status" element={<PjmPollingStatus />} />

                                                <Route path="/nyiso-resources" element={<NyisoResourcesList />} />
                                                <Route
                                                    path="/peak-load-forecast"
                                                    element={
                                                        <PeakLoadForecastProvider>
                                                            <PeakLoadForecast />
                                                        </PeakLoadForecastProvider>
                                                    }
                                                />
                                                <Route path="/admin/data-quality" element={<ReadingsAvailabilityStats />}>
                                                    <Route index element={<Navigate to="by-company" replace />} />
                                                    <Route path="by-company" element={<ReadingsAvailabilityStatsByCompany />} />
                                                    <Route path="by-enrollment" element={<ReadingsAvailabilityStatsByEnrollment />} />
                                                </Route>
                                                <Route path="/admin/new-and-closed-sites" element={<RecentlyCreatedOrClosedSitesReporting />} />
                                                <Route path="/admin/salesforce-products" element={<SalesforceProducts />} />
                                                <Route path="/admin/recently-deleted-objects" element={<RecentlyDeletedObjects />} />
                                                <Route path="/admin/email-utility-automations" element={<EmailUtilityAutomations />} />
                                                <Route
                                                    path="/admin/agreements"
                                                    element={
                                                        <SidebarFilterOptionsProvider>
                                                            <AgreementStatusesSidebarFiltersProvider>
                                                                <AgreementStatusesPage />
                                                            </AgreementStatusesSidebarFiltersProvider>
                                                        </SidebarFilterOptionsProvider>
                                                    }
                                                />
                                                <Route path="/" element={<Dashboard />} />
                                                <Route path="*" element={<Navigate to="/" replace />} />
                                            </Route>
                                        </Route>
                                    </Routes>
                                </AppContextProvider>
                            </BrandingProvider>
                        </ProvideAuth>
                    </CookiesProvider>
                    <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
                </QueryClientProvider>
            </div>
        </AntApp>
    );
}
